import type { Appointment, UpdateAppointmentRequest } from '@ergo/shared';
import { type KeyboardEvent, type MouseEvent, useState } from 'react';
import { api, errorMessage } from '../api';
import { STATUS_LABELS, doneLabel } from '../appointments';
import { CELEBRATION_PALETTES, Toast, hearts, useToast } from '../celebrations';
import { formatDuration } from '../time';

/** The durations she logs almost every time; anything else goes through the free input. */
const DURATION_CHOICES = [30, 45, 60, 90];

const MIN_MINUTES = 5;
const MAX_MINUTES = 600;

interface OutcomeButtonsProps {
  appointment: Pick<Appointment, 'id' | 'status' | 'plannedDurationMin' | 'actualDurationMin' | 'free'>;
  /** Called after a successful PATCH so the caller can refetch. */
  onLogged: () => void;
}

/**
 * The pointage gesture, identical everywhere it appears: Pointage, Aujourd'hui and
 * the editor of a past séance. Unpointée, it is the full row of outcomes; pointée,
 * it collapses to one line — the outcome, the paiement, and the way back to the row.
 *
 * Pointer est la première étape de la note (docs/redesign-plus-douce.md, principe 5) :
 * le geste se fête comme les autres — des cœurs depuis le doigt, et un mot qui dit ce
 * qui vient d'être fait. Revenir en arrière, non : ce n'est pas une étape franchie.
 */
export function OutcomeButtons({ appointment, onLogged }: OutcomeButtonsProps) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otherMinutes, setOtherMinutes] = useState('');
  const { toast, say } = useToast();

  const { status, plannedDurationMin, actualDurationMin, free } = appointment;
  const logged = status !== 'planned';
  const minutes = Number(otherMinutes);
  const minutesAreValid =
    /^\d+$/.test(otherMinutes.trim()) && minutes >= MIN_MINUTES && minutes <= MAX_MINUTES;

  async function log(patch: UpdateAppointmentRequest, from?: { x: number; y: number }) {
    setPending(true);
    setError(null);
    try {
      await api.appointments.update(appointment.id, patch);
      setOtherMinutes('');
      if (from) {
        hearts(from.x, from.y, CELEBRATION_PALETTES.pointage);
        say(patch.free === true ? 'Séance offerte' : 'Séance pointée');
      }
      onLogged();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  /** Les cœurs partent du doigt, pas d'un coin de l'écran : d'où la position du clic. */
  function logFrom(event: MouseEvent, patch: UpdateAppointmentRequest) {
    void log(patch, { x: event.clientX, y: event.clientY });
  }

  function done(actualDurationMin: number): UpdateAppointmentRequest {
    return { status: 'done', actualDurationMin, free: false };
  }

  function logOther(event: MouseEvent | KeyboardEvent<HTMLInputElement>) {
    if (!minutesAreValid) return;
    // Validé au clavier, il n'y a pas de point de tap : le toast suffit à confirmer.
    if ('clientX' in event) logFrom(event, done(minutes));
    else void log(done(minutes));
  }

  function onOtherKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    logOther(event);
  }

  if (logged) {
    return (
      <div className="outcome">
        {/* Là où la séance reste à l'écran une fois pointée — Aujourd'hui, l'éditeur —
            c'est ce mot qui confirme ; au Pointage la ligne s'en va, et c'est la ligne
            qui le dit. */}
        {toast && <Toast>{toast}</Toast>}

        <div className="outcome__logged">
          <strong className="outcome__result">
            {status === 'done' && actualDurationMin !== null
              ? doneLabel(actualDurationMin, free)
              : STATUS_LABELS[status]}
          </strong>
          <button
            type="button"
            className="button button--small button--ghost outcome__revert"
            disabled={pending}
            onClick={() => void log({ status: 'planned', actualDurationMin: null })}
          >
            Changer la durée
          </button>
          {/* Offrir une séance déjà pointée, ou revenir dessus : la durée reste, seul
              le prix change. C'est aussi le chemin d'une séance offerte à durée inhabituelle. */}
          {status === 'done' && (
            <button
              type="button"
              className="button button--small button--ghost outcome__revert"
              disabled={pending}
              onClick={() => void log({ status: 'done', free: !free })}
            >
              {free ? 'Facturer' : 'Offrir'}
            </button>
          )}
        </div>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="outcome">
      {toast && <Toast>{toast}</Toast>}

      <div className="outcome__choices">
        <Choice
          label="Annulée"
          pending={pending}
          onClick={(event) => logFrom(event, { status: 'cancelled' })}
        />
        <Choice
          label="Pas venu·e"
          pending={pending}
          onClick={(event) => logFrom(event, { status: 'no_show' })}
        />
        {/* Un geste, pas un réglage : la séance part faite, offerte, à sa durée prévue.
            Une durée différente se règle après coup avec « Changer la durée » puis « Offrir ». */}
        <Choice
          label="Gratuit"
          pending={pending}
          onClick={(event) =>
            logFrom(event, { status: 'done', actualDurationMin: plannedDurationMin, free: true })
          }
        />
        {DURATION_CHOICES.map((minutes) => (
          <Choice
            key={minutes}
            label={formatDuration(minutes)}
            pending={pending}
            onClick={(event) => logFrom(event, done(minutes))}
          />
        ))}

        <span className="outcome__free">
          <input
            className="input outcome__minutes"
            type="number"
            inputMode="numeric"
            min={MIN_MINUTES}
            max={MAX_MINUTES}
            value={otherMinutes}
            aria-label="Autre durée en minutes"
            disabled={pending}
            onChange={(event) => setOtherMinutes(event.target.value)}
            onKeyDown={onOtherKeyDown}
          />
          <span className="muted">min</span>
          <button
            type="button"
            className="button button--small"
            disabled={pending || !minutesAreValid}
            onClick={logOther}
          >
            OK
          </button>
        </span>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function Choice({
  label,
  pending,
  onClick,
}: {
  label: string;
  pending: boolean;
  onClick: (event: MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      className="button button--small outcome__choice"
      disabled={pending}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
